/**
 * LuvALL Application Version Configuration
 * 
 * Version Numbering Convention (Semantic Versioning):
 * MAJOR.MINOR.PATCH
 * 
 * - MAJOR: Breaking changes, major feature overhauls, or significant UI redesigns
 *   - Increment when: Incompatible API changes, complete redesign, migration required
 *   - Examples: Database schema changes requiring migration, authentication system overhaul
 * 
 * - MINOR: New features, enhancements, or significant improvements (backward compatible)
 *   - Increment when: New functionality added, feature enhancements, new pages/components
 *   - Examples: New tournament types, new statistics features, new social features
 * 
 * - PATCH: Bug fixes, performance improvements, small UI tweaks (backward compatible)
 *   - Increment when: Bug fixes, security patches, minor UI adjustments, typo fixes
 *   - Examples: Fix match scoring bug, improve loading performance, adjust button styling
 * 
 * Build metadata can optionally be appended for internal tracking:
 * - BUILD_DATE: Date of the build (auto-generated or manually set)
 * - RELEASE_NOTES: Brief description of the latest changes
 */

// Declare the build-time constant injected by Vite
declare const __BUILD_TIMESTAMP__: string;

// Get build timestamp - falls back to current time only in dev/HMR scenarios
const getBuildTimestamp = (): string => {
  try {
    return typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : Date.now().toString();
  } catch {
    return Date.now().toString();
  }
};

export const APP_VERSION = {
  MAJOR: 1,
  MINOR: 0,
  PATCH: 1,
  
  // Build metadata - changes with each deployment (injected at build time)
  BUILD_DATE: '2025-01-26',
  BUILD_TIMESTAMP: getBuildTimestamp(),
  
  // Get formatted version string
  get full(): string {
    return `${this.MAJOR}.${this.MINOR}.${this.PATCH}`;
  },
  
  // Get version with 'v' prefix
  get display(): string {
    return `v${this.full}`;
  },
  
  // Get short version (major.minor)
  get short(): string {
    return `${this.MAJOR}.${this.MINOR}`;
  },
  
  // Get unique build identifier (changes on every publish)
  get buildId(): string {
    return `${this.full}-${this.BUILD_TIMESTAMP}`;
  }
} as const;

// Release notes for the current version
export const RELEASE_NOTES = {
  version: APP_VERSION.full,
  date: APP_VERSION.BUILD_DATE,
  highlights: [
    'Initial release of LuvALL',
    'Match tracking and scoring',
    'Group management and polls',
    'Tournament system',
    'Player statistics and ratings'
  ]
};

// Version history for reference
export const VERSION_HISTORY = [
  {
    version: '1.0.0',
    date: '2025-01-01',
    type: 'major' as const,
    description: 'Initial public release'
  }
];

export default APP_VERSION;
